import { MAX_ORIGIN_COUNT, MIN_PLACE_SHARE } from "./constants";
import type { Origin } from "./types";
import { cityKey, displayCity } from "./workspace";

/** Headers that name the home-city column, most specific first. */
const CITY_HEADERS = [
  /^home\s*city$/i,
  /^(city|home|hometown|home\s*town|location|base|home\s*base|based\s*in|office(\s*city)?)$/i,
  /\b(city|location|office|base)\b/i,
  /^(origin|departure(\s*city)?|departing\s*from|flying\s*from)$/i,
];

/** Headers that are never a city, even when they contain a city word ("Home phone", "Department"). */
const NOT_A_CITY =
  /phone|mobile|email|e-mail|name|department|dept|title|role|date|time|\bid\b|zip|postal|address|country|state/i;

/** A street-address column, whose city is read out of each address. "Email address" is not one. */
const ADDRESS_HEADER = /\baddress\b/i;
const NOT_AN_ADDRESS = /e-?mail|web|url|\bip\b/i;

/** A column holding the state or province beside a plain city column. */
const STATE_HEADER = /^(state|province|state\s*\/\s*province|region|st)$/i;

/** Headers whose numbers say how many people a row stands for. */
const COUNT_HEADER = /^(count|travel+ers|people|headcount|attendees|number|#)$/i;

/** A trailing country in an address that says nothing about the city. */
const COUNTRY = /^(usa|u\.s\.a\.|us|u\.s\.|united states( of america)?|canada|uk|united kingdom)$/i;

/** Where the home cities are in a sheet. */
export interface CityColumn {
  index: number;
  /** Whether the first row is a header (skipped) rather than data. */
  hasHeader: boolean;
  /** A state or province column joined to the city, so "Portland" + "OR" resolves to the right one. */
  stateIndex?: number;
  /** The column holds street addresses; the city is read out of each one. */
  address?: boolean;
}

/** The attendee list reduced to what the estimate needs. Names never leave this step. */
export interface AttendeeAggregate {
  /** Home cities as the sheet spells them, with counts; resolved to real places later. */
  origins: Origin[];
  people: number;
  /** Rows with no home city. */
  skipped: number;
}

/** An email or a number: what a data row has and a header row doesn't. */
const DATA_LIKE = /@|\d/;

/**
 * Whether the first row reads as labels ("Name", "Email") rather than a person: a cell is a known
 * label, or the row has no email or number while a row below it does.
 * @param rows - Cell grid.
 * @returns True when the first row is a header.
 * @throws Never.
 */
export const looksLikeHeader = (rows: readonly string[][]): boolean => {
  const [first, ...rest] = rows;
  if (!first) return false;
  if (first.some((cell) => NOT_A_CITY.test(cell) || CITY_HEADERS.some((pattern) => pattern.test(cell.trim())))) {
    return true;
  }
  return !first.some((cell) => DATA_LIKE.test(cell)) && rest.some((row) => row.some((cell) => DATA_LIKE.test(cell)));
};

/**
 * The home-city column a header names, by precedence: "Home city", then a plain city or location
 * header, then one containing a city word, then origin or departure, then a street address. Phone,
 * email, name and department columns never count, so "Home phone" and "Department" are passed
 * over. A plain city column beside a "State" column reads both.
 * @param header - The first row.
 * @returns The column, or null when no header names one.
 * @throws Never.
 */
export const findCityColumn = (header: readonly string[]): Omit<CityColumn, "hasHeader"> | null => {
  for (const pattern of CITY_HEADERS) {
    const index = header.findIndex((cell) => pattern.test(cell.trim()) && !NOT_A_CITY.test(cell));
    if (index === -1) continue;
    const stateIndex = header.findIndex((cell, at) => at !== index && STATE_HEADER.test(cell.trim()));
    return stateIndex === -1 ? { index } : { index, stateIndex };
  }
  const address = header.findIndex((cell) => ADDRESS_HEADER.test(cell) && !NOT_AN_ADDRESS.test(cell));
  return address === -1 ? null : { index: address, address: true };
};

/**
 * The city in a street address: "12 Pine St, Seattle, WA 98101" reads as "Seattle, WA". A trailing
 * country is dropped, and a zip code never counts as the city.
 * @param address - One address cell.
 * @returns The city, with its state when the address has one; empty when none can be read.
 * @throws Never.
 */
export const cityFromAddress = (address: string): string => {
  const parts = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  while (parts.length > 1 && COUNTRY.test(parts[parts.length - 1])) parts.pop();
  if (parts.length === 0) return "";
  const last = parts[parts.length - 1];
  const stateZip = /^([A-Za-z]{2})(?:\s+\d{5}(?:-\d{4})?)?$/.exec(last);
  if (stateZip && parts.length >= 2) return `${parts[parts.length - 2]}, ${stateZip[1].toUpperCase()}`;
  const cityStateZip = /^(.+?)\s+([A-Za-z]{2})\s+\d{5}(?:-\d{4})?$/.exec(last);
  if (cityStateZip) return `${cityStateZip[1]}, ${cityStateZip[2].toUpperCase()}`;
  const city = last.replace(/\s+\d{4,}.*$/, "").trim();
  const before = parts.length >= 2 ? parts[parts.length - 2] : "";
  // "Paris, France" keeps the region; "12 Pine St, Seattle" is a street before the city.
  if (before && !/\d/.test(before)) return /\d/.test(city) ? before : `${before}, ${city}`;
  return /\d/.test(city) ? before : city;
};

/**
 * Finds the home cities in a sheet: the column a header names, else (a sheet without a header)
 * the column whose values are most often real places, when at least half are.
 * @param rows - Cell grid.
 * @param isPlace - Whether a value resolved to a real place.
 * @returns The column, or null when none holds cities.
 * @throws Never.
 */
export const pickCityColumn = (rows: readonly string[][], isPlace: (value: string) => boolean): CityColumn | null => {
  if (rows.length === 0) return null;
  const named = findCityColumn(rows[0]);
  if (named) return { ...named, hasHeader: true };
  // A first row of labels ("Name", "Email") is a header even when none names the city.
  const hasHeader = rows[0].some((cell) => NOT_A_CITY.test(cell) && !isPlace(cell));
  const data = hasHeader ? rows.slice(1) : rows;
  const width = Math.max(...rows.map((row) => row.length));
  let best: { index: number; share: number } | null = null;
  for (let index = 0; index < width; index += 1) {
    if (hasHeader && NOT_A_CITY.test(rows[0][index] ?? "")) continue;
    const values = data.map((row) => (row[index] ?? "").trim()).filter(Boolean);
    if (values.length === 0) continue;
    const share = values.filter(isPlace).length / values.length;
    if (!best || share > best.share) best = { index, share };
  }
  return best && best.share >= 0.5 ? { index: best.index, hasHeader } : null;
};

/**
 * Distinct values of each column, for checking which ones are places before picking a column.
 * @param rows - Cell grid.
 * @param perColumn - How many values to sample from each column.
 * @returns The values.
 * @throws Never.
 */
export const sampleValues = (rows: readonly string[][], perColumn: number): string[] => {
  const width = Math.max(0, ...rows.map((row) => row.length));
  const values = new Set<string>();
  for (let index = 0; index < width; index += 1) {
    const column = [...new Set(rows.map((row) => (row[index] ?? "").trim()).filter(Boolean))];
    column.slice(0, perColumn).forEach((value) => values.add(value));
  }
  return [...values];
};

/**
 * The column most likely to hold home cities when no header names a usable one: the column whose
 * sampled values are most often places, when at least {@link MIN_PLACE_SHARE} of them are. Columns
 * a header rules out (names, emails, departments) are never picked, so a first name that is also a
 * city ("Austin") doesn't make a home-city column, and a list of people with no home cities has none.
 * @param rows - Cell grid, header included.
 * @param perColumn - How many distinct values were sampled from each column, as in {@link sampleValues}.
 * @param isPlace - Whether a sampled value resolved to a real place.
 * @returns The column, or null when no column holds places.
 * @throws Never.
 */
export const placeColumn = (
  rows: readonly string[][],
  perColumn: number,
  isPlace: (value: string) => boolean
): CityColumn | null => {
  const hasHeader = looksLikeHeader(rows);
  const data = hasHeader ? rows.slice(1) : rows;
  const width = Math.max(0, ...data.map((row) => row.length));
  let best: { index: number; share: number } | null = null;
  for (let index = 0; index < width; index += 1) {
    // A "First name" of Austin is a person, not a home city.
    if (hasHeader && NOT_A_CITY.test(rows[0][index] ?? "")) continue;
    const values = [...new Set(data.map((row) => (row[index] ?? "").trim()).filter(Boolean))].slice(0, perColumn);
    if (values.length === 0) continue;
    const share = values.filter(isPlace).length / values.length;
    if (!best || share > best.share) best = { index, share };
  }
  return best && best.share >= MIN_PLACE_SHARE ? { index: best.index, hasHeader } : null;
};

/** The home city a row gives, read as the column says: an address's city, or city plus state. */
const readCity = (row: readonly string[], column: CityColumn): string => {
  const raw = (row[column.index] ?? "").trim();
  if (!raw) return "";
  if (column.address) return cityFromAddress(raw);
  const state = column.stateIndex === undefined ? "" : (row[column.stateIndex] ?? "").trim();
  return state && !raw.includes(",") ? `${raw}, ${state}` : raw;
};

/**
 * The column whose numbers say how many people a row stands for ("Count", "Travelers").
 * @param header - The first row; empty for a sheet without one.
 * @returns The column, or -1 when every row is one person.
 * @throws Never.
 */
export const findCountColumn = (header: readonly string[]): number =>
  header.findIndex((cell) => COUNT_HEADER.test(cell.trim()));

/**
 * How many people a row stands for: its count, when the sheet has a count column and the value
 * is a whole number from 1 to {@link MAX_ORIGIN_COUNT} (capped there), else one.
 * @param row - One data row.
 * @param countColumn - From {@link findCountColumn}.
 * @returns The people the row stands for.
 * @throws Never.
 */
export const rowPeople = (row: readonly string[], countColumn: number): number => {
  if (countColumn === -1) return 1;
  const count = Number((row[countColumn] ?? "").replace(/,/g, ""));
  return Number.isInteger(count) && count > 0 ? Math.min(count, MAX_ORIGIN_COUNT) : 1;
};

/**
 * Merges (city, count) entries into origins, largest first: the same city spelled two ways counts
 * once, and no city exceeds {@link MAX_ORIGIN_COUNT}.
 * @param entries - Cities with how many people each stands for.
 * @returns Origins largest first.
 * @throws Never.
 */
export const mergeOrigins = (entries: ReadonlyArray<{ city: string; count: number }>): Origin[] => {
  const byCity = new Map<string, Origin>();
  for (const entry of entries) {
    const city = entry.city.trim();
    if (!city) continue;
    const count = Number.isInteger(entry.count) && entry.count > 0 ? Math.min(entry.count, MAX_ORIGIN_COUNT) : 1;
    const key = cityKey(city);
    const existing = byCity.get(key);
    byCity.set(key, {
      city: existing?.city ?? displayCity(city),
      count: Math.min((existing?.count ?? 0) + count, MAX_ORIGIN_COUNT),
    });
  }
  return [...byCity.values()].sort((a, b) => b.count - a.count);
};

/**
 * Reduces an attendee sheet to home cities and counts.
 *
 * A "Count" or "Travelers" column, when present, sets how many people a row stands for, so a
 * pre-aggregated sheet (city, count) works as well as one row per person. Counts are whole
 * numbers from 1 to {@link MAX_ORIGIN_COUNT}.
 *
 * @param rows - Cell grid.
 * @param column - Where the home cities are; by default the column the header names.
 * @returns Origins largest first, the people counted, and rows skipped for having no city.
 * @throws Never.
 */
export const aggregateHomeCities = (
  rows: readonly string[][],
  column: CityColumn | null = rows.length ? withHeader(findCityColumn(rows[0])) : null
): AttendeeAggregate => {
  if (!column) return { origins: [], people: 0, skipped: 0 };
  const header = column.hasHeader ? rows[0] : [];
  const dataRows = column.hasHeader ? rows.slice(1) : rows;
  const countColumn = findCountColumn(header);

  const entries: Array<{ city: string; count: number }> = [];
  let skipped = 0;
  for (const row of dataRows) {
    const city = readCity(row, column);
    if (!city) {
      if (row.some((cell) => cell.trim())) skipped += 1;
      continue;
    }
    entries.push({ city, count: rowPeople(row, countColumn) });
  }

  const origins = mergeOrigins(entries);
  return { origins, people: origins.reduce((sum, origin) => sum + origin.count, 0), skipped };
};

const withHeader = (column: Omit<CityColumn, "hasHeader"> | null): CityColumn | null =>
  column ? { ...column, hasHeader: true } : null;

/**
 * How a sheet value reads as a place name: "Seattle WA" becomes "Seattle, WA" so the state narrows it.
 * @param value - A home-city cell.
 * @returns The query to resolve.
 * @throws Never.
 */
export const placeQuery = (value: string): string => value.trim().replace(/^(.+?)\s+([A-Z]{2})$/, "$1, $2");
